import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/satoshi-news";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const satoshiNews: CaseStudy = {
  slug: "satoshi-news",
  title: "Satoshi News",
  cardTitle: "Разработка новостного сайта Satoshi News",
  subtitle: "Медиа о криптовалютах с лентой и аналитикой",
  industry: { label: "Медиа и личный бренд", slug: "media" },
  eyebrow: "Сайт · Онлайн-медиа",
  h1: "Разработка новостного сайта Satoshi News",
  lead:
    "Разработали сайт медиа Satoshi News: новостную ленту, аналитику, «Статью недели», динамический тикер курсов и поиск по материалам с тегами.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить медиа-проект" },

  seo: {
    primaryKeyword: "разработка новостного сайта",
    secondaryKeywords: [
      "разработка сайта онлайн-медиа",
      "создание информационного портала",
      "сайт для редакции",
      "разработка медиа-платформы",
    ],
    semanticEntities: [
      "новостная лента",
      "аналитика",
      "статья недели",
      "тикер курсов",
      "теги материалов",
      "поиск по сайту",
      "просмотры статьи",
    ],
    excludedKeywords: [
      "новости криптовалют",
      "курс биткоина",
      "прогноз биткоина",
      "цена BTC",
    ],
    title: "Новостной сайт Satoshi News — кейс HAY.agency",
    description:
      "Кейс HAY.agency: медиа о криптовалютах с новостной лентой, аналитикой, тегами, поиском и динамическими данными рынка.",
    h1: "Разработка новостного сайта Satoshi News",
    slug: "satoshi-news",
    canonicalUrl: `${SITE_URL}/cases/satoshi-news`,
    ogTitle: "Новостной сайт медиа Satoshi News",
    ogDescription:
      "Новостная лента, аналитика, тикер курсов, теги и поиск по материалам для крипто-медиа.",
    ogImage: `${SITE_URL}${dir}/satoshi-news-og.webp`,
    robotsIndex: true,
    datePublished: "2023-11-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка сайтов",
      to: routes.sites,
      note: "Редакционная структура и динамические данные",
    },
    relatedIndustry: {
      label: "Разработка сайтов для медиа и личного бренда",
      to: routes.sites,
      note: "Редакционная главная и навигация по контенту",
    },
    relatedCases: [
      {
        label: "Разработка сайта для онлайн-сообщества Satoshi Friends",
        to: "/cases/satoshi-friends",
        note: "Личный бренд, медиа и закрытое сообщество",
      },
      {
        label: "Разработка сайта для галереи современного искусства Reportage",
        to: "/cases/reportage",
        note: "Editorial-подача и авторский контент",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Давайте обсудим, как мы можем масштабировать ваш бизнес",
    text: "Оставьте заявку — свяжемся в течение двух часов, чтобы задать вопросы и обсудить задачу.",
    button: "Обсудить медиа-проект",
  },

  client: {
    name: "Satoshi News",
    url: "https://satoshi.ru/",
    city: "Онлайн",
    industry: "Онлайн-медиа",
  },
  year: "2023",
  services: [
    "Дизайн редакционной главной",
    "Разработка новостной ленты и аналитики",
    "Тикер курсов и теги материалов",
    "Поиск по сайту",
  ],
  platform: "Custom",
  tags: [
    { label: "Корпоративный сайт", to: routes.sites },
    { label: "Разработка", to: routes.sites },
  ],
  facts: [
    { label: "Клиент", value: "Satoshi News" },
    { label: "Тип проекта", value: "Новостной сайт онлайн-медиа" },
    { label: "Платформа", value: "Custom" },
    { label: "Год", value: "2023" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Новостная лента, аналитика и «Статья недели» на главной",
      "Динамический тикер курсов BTC, ETH, BNB, XRP",
      "Карточки статей с датой, просмотрами и тематическими тегами",
      "Поиск по материалам и отдельные аналитические статьи",
      "Редакционная иерархия: новости, аналитика, материал недели",
      "Mobile-сценарий чтения статьи, ленты и навигации",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Satoshi News — медиа о криптовалютах с постоянным потоком новостей и аналитики. Нужна была редакционная главная, которая одновременно показывает свежие материалы, аналитику и выделяет «Статью недели», не превращаясь в бесконечную ленту.",
        "Отдельная задача — встроить в интерфейс живые рыночные данные: тикер курсов должен работать как функциональный элемент, а не декоративная вставка.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: редакционная композиция на графитовой подложке",
    paragraphs: [
      "Интерфейс собран на мягкой графитовой подложке — крупная новость, тикер и лента читаются как единая редакционная структура.",
      "Карточки материалов унифицированы по дате, просмотрам и тегам, что упрощает навигацию по большому объёму контента.",
    ],
    bullets: [
      "Графитовая подложка вместо чистого белого",
      "Единая иерархия: новость, аналитика, статья недели",
      "Функциональный тикер курсов на главной",
    ],
  },

  identity: {
    fonts: [
      { name: "Inter", role: "Заголовки", sample: "Satoshi News" },
      { name: "Inter", role: "Интерфейс и текст", sample: "Статья недели" },
    ],
    colors: [
      { hex: "#1B1C1F", name: "Фон" },
      { hex: "#3DDC84", name: "Акцент тикера" },
      { hex: "#F0F0F0", name: "Текст" },
    ],
  },

  cover: img(
    "satoshi-news-case-cover",
    "Главная страница новостного сайта Satoshi News с тикером курсов",
    6,
  ),

  gallery: [
    img("satoshi-news-feed-tags", "Новостная лента и теги материалов на сайте Satoshi News", 6),
    img("satoshi-news-article", "Страница статьи с датой, просмотрами и тегами Satoshi News", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия выстроена в 2–3 экрана: лента материалов, навигация по тегам и чтение статьи.",
      "Тикер курсов и карточки материалов на мобильном показаны с реальными заполненными данными.",
    ],
    images: [
      mobile("satoshi-news-mobile-feed", "Мобильная новостная лента Satoshi News"),
      mobile("satoshi-news-mobile-article", "Мобильное чтение статьи на сайте Satoshi News"),
    ],
  },

  columnBlocks: [
    {
      id: "editorial",
      title: "Редакционная главная",
      paragraphs: [
        "Главная выстроена по иерархии: свежие новости, аналитика и отдельно выделенный материал недели.",
      ],
      bullets: ["Свежие новости", "Аналитика", "Статья недели"],
    },
    {
      id: "navigation",
      title: "Навигация по контенту",
      paragraphs: [
        "Теги, поиск и карточки материалов помогают быстро находить нужную статью в большом потоке контента.",
      ],
      bullets: ["Теги материалов", "Поиск по сайту", "Карточки статей с датой и просмотрами"],
    },
    {
      id: "ticker",
      title: "Динамические данные",
      paragraphs: [
        "Рыночный тикер курсов BTC, ETH, BNB и XRP работает как функциональный элемент интерфейса, а не статичная вставка.",
      ],
      bullets: ["Тикер BTC, ETH, BNB, XRP", "Обновляемые данные на главной"],
    },
    {
      id: "mobile-reading",
      title: "Чтение на мобильном",
      paragraphs: [
        "Статья, лента и навигация уложены в 2–3 mobile-экрана без потери редакционной структуры.",
      ],
      bullets: ["Экран статьи", "Экран ленты", "Экран навигации по тегам"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка новостного сайта", to: routes.sites },
    ],
  },
};
